import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { t } from "../i18n";

export default function ProfilePage({ lang }) {
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [_loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("fm_token");
    if (!token) {
      setLoading(false);
      return;
    }

    Promise.all([
      fetch("/api/user/profile", {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : null)),
      fetch("/api/user/download-quota", {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([userData, quotaData]) => {
        setUser(userData?.user || null);
        setQuota(quotaData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isCreator = quota?.is_creator || false;
  const used = quota?.downloads_used || 0;
  const limit = quota?.downloads_limit || 20;
  const totalCredits = quota?.total_credits_purchased || 0;
  const creditsUsed = quota?.credits_used || 0;
  const creditsRemaining = quota?.credits_remaining || 0;
  const purchases = quota?.credit_purchases || [];
  // 每月剩余 = limit - used（已用优先扣月配额）
  const monthlyRemaining = Math.max(0, limit - used);
  const percent = limit > 0 ? Math.round((used / limit) * 100) : 0;

  // 用户信息
  const displayName = user?.name || user?.email || "";
  const avatarLetter = displayName ? displayName[0].toUpperCase() : "?";

  // 重置日
  let resetDay = "";
  if (quota?.reset_at) {
    try { resetDay = new Date(quota.reset_at).getDate().toString(); } catch (_) { /* noop */ }
  }

  // 有效期
  let expiresDate = "";
  if (quota?.reset_at && isCreator) {
    try { expiresDate = quota.reset_at.slice(0, 10); } catch (_) { /* noop */ }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{t(lang, "profile")} — FreeMusic.Top</title>
      </Helmet>

      {/* ── User Header ── */}
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white px-6 pt-10 pb-6">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/40 object-cover flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-xl font-bold flex-shrink-0">
              {avatarLetter}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-bold truncate">{user?.name || user?.email || "User"}</h1>
            {user?.email && <p className="text-indigo-100 text-sm truncate mt-0.5">{user.email}</p>}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 -mt-4 space-y-4 pb-12">

        {/* ═══════════════ ① Current Plan ═══════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {t(lang, "currentPlan")}
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                isCreator ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"
              }`}>
                {isCreator ? <>🌟 {t(lang, "creatorTier")}</> : <>{t(lang, "freePlan")}</>}
              </span>
              {isCreator ? (
                <p className="text-sm text-gray-500 mt-2">
                  ✅ {t(lang, "quotaUnlimited")} {t(lang, "download")}
                </p>
              ) : (
                <p className="text-sm text-gray-500 mt-2">
                  {limit} {t(lang, "download")}/{t(lang, "perMonth").replace("/", "")}
                </p>
              )}
            </div>
            {isCreator && expiresDate && (
              <div className="text-right text-xs text-gray-400">
                <div>{t(lang, "nextBilling")}</div>
                <div className="font-medium text-gray-600">{expiresDate}</div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════ ② 月度赠送 ═══════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {t(lang, "monthlyDownloads")}
          </h2>
          {isCreator ? (
            <div className="flex items-center gap-2.5 text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="text-sm font-medium">{t(lang, "quotaUnlimited")}</span>
              <span className="text-xs text-emerald-400 ml-auto">({t(lang, "neverExpire")})</span>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-gray-500">{t(lang, "quotaUsed")}</span>
                <span className={`font-medium ${used >= limit ? "text-red-500" : "text-gray-900"}`}>
                  {used} / {limit}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${used >= limit ? "bg-red-400" : "bg-indigo-500"}`}
                  style={{ width: `${Math.min(percent, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span><span className="font-medium text-gray-600">{monthlyRemaining}</span> {t(lang, "quotaRemaining")}</span>
                <span>{t(lang, "resetsOn")} {resetDay ? `${resetDay}${lang === "zh" ? "日" : "th"}` : ""}</span>
              </div>
            </div>
          )}
        </div>

        {/* ═══════════════ ③ 购买点数 ═══════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            💎 {t(lang, "bonusCredits")}
          </h2>

          {totalCredits > 0 || creditsRemaining > 0 ? (
            <>
              {/* 总 / 用 / 余 */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <div className="text-lg font-bold text-gray-900">{totalCredits}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t(lang, "totalPurchased") || "Total"}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <div className="text-lg font-bold text-amber-500">{creditsUsed}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t(lang, "quotaUsed")}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <div className="text-lg font-bold text-indigo-600">{creditsRemaining}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t(lang, "quotaRemaining")}</div>
                </div>
              </div>

              {/* 购买记录（折叠） */}
              {purchases.length > 0 && (
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setHistoryOpen(!historyOpen)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <span>{t(lang, "purchaseHistory") || "Purchase History"} ({purchases.length})</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${historyOpen ? "rotate-180" : ""}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {historyOpen && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {purchases.map((p) => (
                        <div key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                          <div className="text-gray-500">
                            {p.created_at ? p.created_at.slice(0, 10) : "—"}
                          </div>
                          <div className="text-gray-700">
                            {p.amount_cents === 100 ? "$1" : `$${(p.amount_cents / 100).toFixed(2)}`}
                          </div>
                          <div className="font-medium text-indigo-600">+{p.credits_bought}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 py-2">{t(lang, "noCredits") || "No credits purchased yet"}</p>
          )}
        </div>

        {/* ═══════════════ ④ 订阅计划 ═══════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            🚀 {t(lang, "pricingOptions")}
          </h2>

          {isCreator ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="text-sm font-medium">{t(lang, "quotaUnlimited")} {t(lang, "download")}</span>
              </div>
              <button
                onClick={() => window.location.href = "/?showPricing=1"}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium rounded-xl transition"
              >
                {t(lang, "manageSubscription")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {/* Monthly */}
              <div className={`rounded-xl border-2 p-4 border-gray-100 bg-white`}>
                <div className="text-lg font-bold text-gray-900">$3</div>
                <div className="text-sm text-gray-500">{t(lang, "perMonth")}</div>
                <ul className="text-xs text-gray-600 mt-3 space-y-1.5">
                  <li>✅ {t(lang, "quotaUnlimited")} {t(lang, "download")}</li>
                  <li>✅ {t(lang, "creatorFeature2")?.replace("Unlimited downloads", "MP3+WAV")}</li>
                  <li>✅ {t(lang, "creatorFeature1")?.replace("Ad-free listening", "Ad-free")}</li>
                </ul>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("fm_token");
                      if (!token) return;
                      const r = await fetch("/api/stripe/create-checkout-session", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ price_type: "monthly" }),
                      });
                      const data = await r.json();
                      if (data.url) window.location.href = data.url;
                    } catch (_) { /* noop */ }
                  }}
                  className="mt-4 w-full px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition"
                >
                  {t(lang, "subscribe") || "Subscribe"}
                </button>
              </div>
              {/* Yearly */}
              <div className={`rounded-xl border-2 p-4 relative border-indigo-500 bg-indigo-50`}>
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[10px] font-semibold px-3 py-0.5 rounded-full">
                  {t(lang, "savePct") || "Save 17%"}
                </div>
                <div className="text-lg font-bold text-gray-900">$30</div>
                <div className="text-sm text-gray-500">{t(lang, "perYear") || "/year"}</div>
                <ul className="text-xs text-gray-600 mt-3 space-y-1.5">
                  <li>✅ {t(lang, "quotaUnlimited")} {t(lang, "download")}</li>
                  <li>✅ {t(lang, "creatorFeature2")?.replace("Unlimited downloads", "MP3+WAV")}</li>
                  <li>✅ {t(lang, "creatorFeature1")?.replace("Ad-free listening", "Ad-free")}</li>
                </ul>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("fm_token");
                      if (!token) return;
                      const r = await fetch("/api/stripe/create-checkout-session", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ price_type: "yearly" }),
                      });
                      const data = await r.json();
                      if (data.url) window.location.href = data.url;
                    } catch (_) { /* noop */ }
                  }}
                  className="mt-4 w-full px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition"
                >
                  {t(lang, "subscribe")}
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
